import type { Alert } from '@didaskill/domain/alerts/types';
import type { AssignmentLesson, McqQuestionRequest } from '@didaskill/domain/assignments/types';
import type { CourseViewFull, CourseViewMinimal } from '@didaskill/domain/courses/types';
import type { LearningResource } from '@didaskill/domain/learning-resources/types';
import type {
  CreateEditableSequenceBody,
  CreateLessonBody,
  CreateLessonEditableVersionBody,
  CreateResourcesLessonBody,
  GetLessonCoursesQuery,
  GetLessonQuery,
  GetLessonsQuery,
  GetLessonVersionQuery,
  LessonAssignment,
  LessonEditableVersionSequence,
  LessonEditableVersionViewFull,
  LessonEditableVersionViewFullNumberedTitled,
  LessonVersionViewFull,
  LessonVersionViewFullNumberedTitled,
  LessonVersionWithSequencesViewMinimal,
  LessonVersionWithSequencesViewMinimalNumberedTitled,
  LessonViewFull,
  LessonViewFullNumberedTitled,
  LessonViewInternal,
  LessonViewList,
  LessonViewMinimal,
  UpdateEditableSequenceBody,
  UpdateLessonBody,
  UpdateLessonEditableVersionBody,
  UpdateLessonQuery,
  UpsertLessonAssignmentBody,
} from '@didaskill/domain/lessons/types';
import type { LessonFeedbackStatistics } from '@didaskill/domain/notations/types';
import type { VersionObjectBase } from '@didaskill/domain/platform-types';
import type { EmployeeViewMinimal } from '@didaskill/domain/users/types';
import { serializeVersion } from '@didaskill/domain/versions';
import type { TypedResponse } from '@didaskill/types/misc';

import { HttpRepository } from '../httpRepository';
import { mapLesson, mapVersion } from './mappers';

const e = encodeURIComponent;

const LESSONS_API_URL = '/api/v1/school/lessons';
const LESSON_FILES_API_URL = '/api/v1/file/downloads/lessons';

export class LessonsRepository extends HttpRepository {
  /**
   * Fetch lessons.
   *
   * @param query - Query.
   * @param query.q - Query string (filters on title or code).
   * @param query.view - View to fetch (LESSON_VIEWS enum).
   * @param query.sort - Field to sort (field is ASC, -field is DESC).
   * @param query.limit - Limit number of results.
   * @param query.onlyPublished - Filter out lessons without published versions.
   * @param query.responsibleIds - Course manager user ids.
   * @param query.skillIds - Skill ids.
   * @param query.skillTagIds - Skill tag ids.
   * @param query.minOngoingAlertsCount - Inclusive minimum number of ongoing alerts.
   * @param query.maxOngoingAlertsCount - Inclusive maximum number of ongoing alerts.
   * @param query.withoutResponsible - Filter lessons that have no course manager.
   * @param query.withResponsible - Filter lessons that have at least one course manager.
   * @param query.statuses - Edition statuses. Lessons matching any of these statuses are returned.
   * @returns Lessons.
   */
  async getLessons(query: GetLessonsQuery) {
    const { body } = await this.http.get(LESSONS_API_URL).query(query);

    return body as {
      count: number;
      lessons:
        | LessonViewMinimal[]
        | LessonViewList[]
        | LessonViewInternal[]
        | LessonViewFullNumberedTitled[];
    };
  }

  /**
   * Get a lesson by its id.
   *
   * @param string lessonId - Lesson id.
   * @param query - Query.
   * @param query.view - View to fetch (LESSON_VIEWS enum).
   * @returns Lesson.
   */
  async getLesson(
    lessonId: string,
    query: GetLessonQuery,
  ): Promise<LessonViewMinimal | LessonViewInternal | LessonViewFullNumberedTitled> {
    const { body: lesson } = await this.http.get(`${LESSONS_API_URL}/${e(lessonId)}`).query(query);

    return mapLesson(lesson as LessonViewFull | LessonViewMinimal | LessonViewInternal);
  }

  /**
   * Fetch aggregated sequence feedback for a lesson.
   * @param lessonId Lesson id.
   * @returns Likes, dislikes, reports and per-sequence comments.
   */
  async getLessonFeedbackStatistics(lessonId: string): Promise<LessonFeedbackStatistics> {
    const { body } = await this.http.get(`${LESSONS_API_URL}/${e(lessonId)}/feedback`);

    return body as LessonFeedbackStatistics;
  }

  /**
   * Create a new lesson.
   *
   * @param params - Params.
   * @param params.title - Lesson title.
   * @param params.code - Lesson code.
   * @param params.editors - Lesson editor ids.
   * @returns Lesson.
   */
  async createLesson(params: CreateLessonBody): Promise<LessonViewFullNumberedTitled> {
    const { body: lesson } = await this.http.post(LESSONS_API_URL).send(params);

    return mapLesson(lesson as LessonViewFull) as LessonViewFullNumberedTitled;
  }

  /**
   * Delete a lesson.
   *
   * @param lessonId - Lesson id.
   */
  async deleteLesson(lessonId: string) {
    await this.http.delete(`${LESSONS_API_URL}/${e(lessonId)}`);
  }

  /**
   * Update lesson metadata.
   *
   * @param lessonId - Lesson id.
   * @param params - Params.
   * @param params.title - Lesson title.
   * @param query - Query.
   * @param query.view - Lesson view to return.
   * @returns Updated lesson.
   */
  async updateLesson(
    lessonId: string,
    params: UpdateLessonBody,
    query?: UpdateLessonQuery,
  ): Promise<LessonViewMinimal | LessonViewInternal | LessonViewFull> {
    const { body: lesson } = await this.http
      .patch(`${LESSONS_API_URL}/${e(lessonId)}`)
      .send(params)
      .query(query);

    return mapLesson(lesson as LessonViewFull | LessonViewMinimal | LessonViewInternal);
  }

  /**
   * Get a lesson version by its number.
   *
   * @param lessonId - Lesson id.
   * @param version - Version.
   * @param version.versionMajor - Major version number.
   * @param version.versionMinor - Minor version number.
   * @returns Lesson version.
   */
  async getLessonVersion(
    lessonId: string,
    version: VersionObjectBase<false>,
    query?: GetLessonVersionQuery,
  ): Promise<
    LessonVersionViewFullNumberedTitled | LessonVersionWithSequencesViewMinimalNumberedTitled
  > {
    const serializedVersion = serializeVersion(version);

    const { body: lessonVersion } = await this.http
      .get(`${LESSONS_API_URL}/${e(lessonId)}/versions/${e(serializedVersion)}`)
      .query(query);

    return mapVersion(
      lessonVersion as LessonVersionViewFull | LessonVersionWithSequencesViewMinimal,
    ) as LessonVersionViewFullNumberedTitled | LessonVersionWithSequencesViewMinimalNumberedTitled;
  }

  /**
   * Get a lesson editable version by its id.
   *
   * @param lessonId - Lesson id.
   * @param editableVersionId - Editable version id.
   * @returns Lesson editable version.
   */
  async getLessonEditableVersion(
    lessonId: string,
    editableVersionId: string,
  ): Promise<LessonEditableVersionViewFullNumberedTitled> {
    const { body: editableVersion } = await this.http.get(
      `${LESSONS_API_URL}/${e(lessonId)}/editable-versions/${e(editableVersionId)}`,
    );

    return mapVersion(
      editableVersion as LessonEditableVersionViewFull,
    ) as LessonEditableVersionViewFullNumberedTitled;
  }

  /**
   * Create a new editable version.
   *
   * @param lessonId - Lesson id.
   * @param params - Params.
   * @param params.fromVersionMajor - Origin major version.
   * @param params.fromVersionMinor - Origin major minor.
   * @param params.editors - Editor ids of this version.
   * @param params.type - Type of version (MAJOR|MINOR).
   * @returns Editable version.
   */
  async createLessonEditableVersion(
    lessonId: string,
    params: CreateLessonEditableVersionBody,
  ): Promise<LessonEditableVersionViewFullNumberedTitled> {
    const { body: editableVersion } = await this.http
      .post(`${LESSONS_API_URL}/${e(lessonId)}/editable-versions`)
      .send(params);

    return mapVersion(
      editableVersion as LessonEditableVersionViewFull,
    ) as LessonEditableVersionViewFullNumberedTitled;
  }

  /**
   * Edit a lesson editable version metadata.
   *
   * @param lessonId - Lesson id.
   * @param editableVersionId - Editable version id.
   * @param params - Params.
   * @param params.editors - Editor user ids.
   * @param params.status - Editable version status.
   * @param params.sequences - Sequences in order.
   * @param params.publish - Set to true to publish the version.
   * @returns Editable version or published version.
   */
  async updateLessonEditableVersion(
    lessonId: string,
    editableVersionId: string,
    params: UpdateLessonEditableVersionBody,
  ): Promise<LessonVersionViewFullNumberedTitled | LessonEditableVersionViewFullNumberedTitled> {
    const { body: editableVersion } = await this.http
      .patch(`${LESSONS_API_URL}/${e(lessonId)}/editable-versions/${e(editableVersionId)}`)
      .send(params);

    return mapVersion(editableVersion as LessonEditableVersionViewFull | LessonVersionViewFull) as
      | LessonVersionViewFullNumberedTitled
      | LessonEditableVersionViewFullNumberedTitled;
  }

  /**
   * Delete a lesson editable version.
   *
   * @param lessonId - Lesson id.
   * @param editableVersionId - Editable version id.
   */
  async deleteLessonEditableVersion(lessonId: string, editableVersionId: string) {
    await this.http.delete(
      `${LESSONS_API_URL}/${e(lessonId)}/editable-versions/${e(editableVersionId)}`,
    );
  }

  /**
   * Create a new empty editable sequence.
   *
   * @param lessonId - Lesson id.
   * @param editableVersionId - Editable version id.
   * @param params - Body.
   * @param params.type - Sequence type.
   * @returns Lesson editable version.
   */
  async createEditableSequence(
    lessonId: string,
    editableVersionId: string,
    params?: CreateEditableSequenceBody,
  ): Promise<LessonEditableVersionSequence> {
    const { body: editableSequence } = await this.http
      .post(`${LESSONS_API_URL}/${e(lessonId)}/editable-versions/${e(editableVersionId)}/sequences`)
      .send(params);

    return editableSequence;
  }

  /**
   * Update an editable sequence.
   *
   * @param lessonId - Lesson id.
   * @param editableVersionId - Editable version id.
   * @param sequenceVersionId - Sequence version id.
   * @param params - Params.
   * @param params.content - Content.
   * @param params.isNumbered - Is the sequence numbered.
   * @returns Updated sequence.
   */
  async updateEditableSequence(
    lessonId: string,
    editableVersionId: string,
    sequenceVersionId: string,
    params: UpdateEditableSequenceBody,
  ): Promise<LessonEditableVersionSequence> {
    const { body: editableSequence } = await this.http
      .patch(
        `${LESSONS_API_URL}/${e(lessonId)}/editable-versions/${e(
          editableVersionId,
        )}/sequences/${e(sequenceVersionId)}`,
      )
      .send(params);

    return editableSequence;
  }

  /**
   * Delete an editable sequence.
   *
   * @param lessonId - Lesson id.
   * @param editableVersionId - Editable version id.
   * @param sequenceVersionId - Sequence version id.
   */
  async deleteEditableSequence(
    lessonId: string,
    editableVersionId: string,
    sequenceVersionId: string,
  ) {
    await this.http.delete(
      `${LESSONS_API_URL}/${e(lessonId)}/editable-versions/${e(
        editableVersionId,
      )}/sequences/${e(sequenceVersionId)}`,
    );
  }

  /**
   * Reset an editable sequence to its origin version.
   *
   * @param lessonId - Lesson id.
   * @param editableVersionId - Editable version id.
   * @param sequenceVersionId - Editable sequence version id.
   * @returns - Request response with updated editable sequence.
   */
  async resetEditableSequence(
    lessonId: string,
    editableVersionId: string,
    sequenceVersionId: string,
  ) {
    return (await this.http.post(
      `${LESSONS_API_URL}/${e(lessonId)}/editable-versions/${e(
        editableVersionId,
      )}/sequences/${e(sequenceVersionId)}/reset`,
    )) as TypedResponse<LessonEditableVersionSequence>;
  }

  /**
   * Add a question to an editable sequence exercise.
   *
   * @param lessonId - Lesson id.
   * @param editableVersionId - Editable version id.
   * @param sequenceVersionId - Sequence version id.
   * @param question - Question data.
   * @returns Updated sequence.
   */
  async createExerciseQuestion(
    lessonId: string,
    editableVersionId: string,
    sequenceVersionId: string,
    question: McqQuestionRequest,
  ): Promise<LessonEditableVersionSequence> {
    const { body: editableSequence } = await this.http
      .post(
        `${LESSONS_API_URL}/${e(lessonId)}/editable-versions/${e(
          editableVersionId,
        )}/sequences/${e(sequenceVersionId)}/exercise/questions`,
      )
      .send(question);

    return editableSequence;
  }

  /**
   * Import questions from a CSV file into an editable sequence exercise.
   *
   * @param lessonId - Lesson id.
   * @param editableVersionId - Editable version id.
   * @param sequenceVersionId - Sequence version id.
   * @param file - CSV file.
   * @returns Updated sequence.
   */
  async importExerciseQuestionsCsv(
    lessonId: string,
    editableVersionId: string,
    sequenceVersionId: string,
    file: File,
  ): Promise<LessonEditableVersionSequence> {
    const { body: editableSequence } = await this.http
      .post(
        `${LESSONS_API_URL}/${e(lessonId)}/editable-versions/${e(
          editableVersionId,
        )}/sequences/${e(sequenceVersionId)}/exercise/questions/import/csv`,
      )
      .attach('file', file, file.name);

    return editableSequence;
  }

  /**
   * Update a question in an editable sequence exercise.
   *
   * @param lessonId - Lesson id.
   * @param editableVersionId - Editable version id.
   * @param sequenceVersionId - Sequence version id.
   * @param questionId - Question id.
   * @param question - Question data.
   * @returns Updated sequence.
   */
  async updateExerciseQuestion(
    lessonId: string,
    editableVersionId: string,
    sequenceVersionId: string,
    questionId: string,
    question: McqQuestionRequest,
  ): Promise<LessonEditableVersionSequence> {
    const { body: editableSequence } = await this.http
      .patch(
        `${LESSONS_API_URL}/${e(lessonId)}/editable-versions/${e(
          editableVersionId,
        )}/sequences/${e(sequenceVersionId)}/exercise/questions/${e(questionId)}`,
      )
      .send(question);

    return editableSequence;
  }

  /**
   * Remove a question in an editable sequence exercise.
   *
   * @param lessonId - Lesson id.
   * @param editableVersionId - Editable version id.
   * @param sequenceVersionId - Sequence version id.
   * @param questionId - Question id.
   * @returns Updated sequence.
   */
  async deleteExerciseQuestion(
    lessonId: string,
    editableVersionId: string,
    sequenceVersionId: string,
    questionId: string,
  ): Promise<LessonEditableVersionSequence> {
    const { body: editableSequence } = await this.http.delete(
      `${LESSONS_API_URL}/${e(lessonId)}/editable-versions/${e(
        editableVersionId,
      )}/sequences/${e(sequenceVersionId)}/exercise/questions/${e(questionId)}`,
    );

    return editableSequence;
  }

  /**
   * Link an assignment to lesson major versions.
   *
   * @param lessonId - Lesson id.
   * @param params - Params.
   * @param params.assignmentId - Assignment id.
   * @param params.forVersionsMajor - Lesson major versions.
   * @returns Assignment with all major versions.
   */
  async upsertLessonAssignment(
    lessonId: string,
    params: UpsertLessonAssignmentBody,
  ): Promise<LessonAssignment> {
    const { body: lessonAssignment } = await this.http
      .post(`${LESSONS_API_URL}/${e(lessonId)}/assignments`)
      .send(params);

    return lessonAssignment;
  }

  /**
   * Unlink lesson versions from an assignment.
   *
   * @param lessonId - Lesson id.
   * @param params - Params.
   * @param params.assignmentId - Assignment id.
   * @returns Updated lesson assignments
   */
  async unlinkLessonVersionsFromAssignment(
    lessonId: string,
    params: UpsertLessonAssignmentBody,
  ): Promise<AssignmentLesson[]> {
    const { body: assignmentLessons } = await this.http
      .patch(`${LESSONS_API_URL}/${e(lessonId)}/assignments`)
      .send(params);

    return assignmentLessons;
  }

  /**
   * Get courses linked to a lessonId.
   *
   * @param lessonId - Lesson id.
   * @returns List of courses linked to a lesson.
   */
  async getLessonCourses<T extends CourseViewMinimal[] | CourseViewFull[]>(
    lessonId: string,
    query?: GetLessonCoursesQuery,
  ): Promise<T> {
    const { body: lessonCourses } = await this.http
      .get(`${LESSONS_API_URL}/${e(lessonId)}/courses`)
      .query(query);

    return lessonCourses;
  }

  /**
   * Get resources linked to a lessonId.
   *
   * @param lessonId - Lesson id.
   * @returns List of resources linked to a lesson.
   */
  async getLessonResources(
    lessonId: string,
  ): Promise<{ count: number; learningResources: LearningResource[] }> {
    const { body: lessonResources } = await this.http.get(
      `${LESSONS_API_URL}/${e(lessonId)}/resources`,
    );

    return lessonResources;
  }

  /**
   * Link an existing learning resource to a lesson.
   *
   * @param lessonId - Lesson id.
   * @param params - Body.
   */
  async createLessonResource(lessonId: string, params: CreateResourcesLessonBody): Promise<void> {
    await this.http.post(`${LESSONS_API_URL}/${e(lessonId)}/resources`).send(params);
  }

  /**
   * Unlink a learning resource from a lesson.
   *
   * @param lessonId - Lesson id.
   * @param learningResourceId - Learning resource id.
   */
  async deleteLessonResource(lessonId: string, learningResourceId: string): Promise<void> {
    await this.http.delete(`${LESSONS_API_URL}/${e(lessonId)}/resources/${e(learningResourceId)}`);
  }

  /**
   * Run broken links job on a lesson.
   * @param lessonId - Lesson id.
   * @returns Alerts found.
   */
  async runBrokenLinksJobOnLesson(lessonId: string): Promise<TypedResponse<Alert[]>> {
    const res = await this.http.post(`${LESSONS_API_URL}/${e(lessonId)}/jobs/broken-links`);

    return res;
  }

  /**
   * Download lesson version sequences file or files.
   * @param lessonId - Lesson id.
   * @param version - Version.
   * @returns - File or zip archive containing the sequences files.
   */
  async downloadLessonVersionFile(
    lessonId: string,
    version: VersionObjectBase<false>,
  ): Promise<{ type: string; body: Blob }> {
    const serializedVersion = serializeVersion(version);

    const { body, type } = await this.http
      .get(`${LESSON_FILES_API_URL}/${e(lessonId)}/versions/${e(serializedVersion)}/sequences`)
      .responseType('blob');

    return { body, type };
  }

  /**
   * Download the pre-generated PDF for a single lesson sequence when it exists on storage.
   *
   * @returns Response body and Content-Disposition when the PDF exists (HTTP 200), or null when
   *   it is not available yet (HTTP 404).
   */
  async downloadLessonSequencePdfIfAvailable(
    lessonId: string,
    version: VersionObjectBase<false>,
    sequenceVersionId: string,
  ): Promise<{ blob: Blob; contentDisposition: string | undefined } | null> {
    const serializedVersion = serializeVersion(version);

    const response = await this.http
      .get(
        `${LESSON_FILES_API_URL}/${e(lessonId)}/versions/${e(
          serializedVersion,
        )}/sequences?sequenceVersionId=${e(sequenceVersionId)}`,
      )
      .responseType('blob')
      .ok((res) => res.status === 200 || res.status === 404);

    if (response.status === 404) {
      return null;
    }

    return {
      blob: response.body as Blob,
      contentDisposition: response.headers['content-disposition'] as string | undefined,
    };
  }

  /**
   * Request a PDF generation for a lesson version.
   *
   * @param lessonId - Lesson id.
   * @param version - Version.
   */
  async requestLessonVersionPdfGeneration(
    lessonId: string,
    version: VersionObjectBase<false>,
  ): Promise<void> {
    const serializedVersion = serializeVersion(version);

    await this.http
      .post(
        `${LESSONS_API_URL}/${e(lessonId)}/versions/${e(serializedVersion)}/pdf-generation-requests`,
      )
      .send({});
  }

  /**
   * Clears the published audio of a lesson version and requests a new generation.
   * @param lessonId Lesson id.
   * @param version Published version to regenerate.
   * @returns Nothing. The generation continues asynchronously.
   */
  async requestLessonVersionAudioRegeneration(
    lessonId: string,
    version: VersionObjectBase<false>,
  ): Promise<void> {
    const serializedVersion = serializeVersion(version);

    await this.http
      .post(
        `${LESSONS_API_URL}/${e(lessonId)}/versions/${e(serializedVersion)}/audio-regeneration-requests`,
      )
      .send({});
  }

  /**
   * Patch a sequence block content.
   * @param lessonId - Lesson id.
   * @param version - Version.
   * @param sequenceVersionId - Sequence version id.
   * @param blockId - Block id.
   * @param href - Href.
   * @param hrefToUpdate - Href to update.
   */
  async patchSequenceBlock(
    lessonId: string,
    version: VersionObjectBase<false>,
    sequenceVersionId: string,
    blockId: string,
    {
      href,
      hrefToUpdate,
    }: {
      href: string;
      hrefToUpdate: string;
    },
  ): Promise<void> {
    const serializedVersion = serializeVersion(version);

    await this.http
      .patch(
        `${LESSONS_API_URL}/${e(lessonId)}/versions/${e(serializedVersion)}/sequences/${e(
          sequenceVersionId,
        )}/blocks/${e(blockId)}`,
      )
      .send({ href, hrefToUpdate });
  }

  /**
   * Add a responsible user to a lesson.
   *
   * @param lessonId - Lesson id.
   * @param userId - User id to add as responsible.
   * @returns Added responsible as minimal employee view.
   */
  async addResponsibleToLesson(lessonId: string, userId: string): Promise<EmployeeViewMinimal> {
    const { body: user } = await this.http.post(
      `${LESSONS_API_URL}/${e(lessonId)}/responsibles/${e(userId)}`,
    );

    return user as EmployeeViewMinimal;
  }

  /**
   * Remove a responsible user from a lesson.
   *
   * @param lessonId - Lesson id.
   * @param userId - User id to remove from responsibles.
   */
  async removeResponsibleFromLesson(lessonId: string, userId: string): Promise<void> {
    await this.http.delete(`${LESSONS_API_URL}/${e(lessonId)}/responsibles/${e(userId)}`);
  }

  /**
   * Add skills to a lesson.
   *
   * @param lessonId - Lesson id.
   * @param skillIds - Skill ids to add.
   */
  async addLessonSkills(lessonId: string, skillIds: string[]): Promise<void> {
    await this.http.post(`${LESSONS_API_URL}/${e(lessonId)}/skills`).send({ skillIds });
  }

  /**
   * Remove a skill from a lesson.
   *
   * @param lessonId - Lesson id.
   * @param skillId - Skill id to remove.
   */
  async deleteLessonSkill(lessonId: string, skillId: string): Promise<void> {
    await this.http.delete(`${LESSONS_API_URL}/${e(lessonId)}/skills/${e(skillId)}`);
  }
}
